import { Datetime, getClasses } from '@/utils';
import { ForwardRefExoticComponent, forwardRef, useCallback } from 'react';
import { initCausedByPopupState, useCausedByPopupState } from '@/features/IssueHistory/components/CausedByPopover';

import { DispatchIssueWithCacheFlightLateEnum } from '@/models/gen/graphql';
import SimpleTableCell from '@/components/SimpleTable/SimpleTableCell';
import SimpleTableRow from '@/components/SimpleTable/SimpleTableRow';
import { useIssueHistory } from '@/features/IssueHistory';
import { useIssueHistoryDrawer } from '@/features/IssueHistory/components/IssueHistoryDrawer';

const IssueHistoryTableRow = ({ id }: { id: string }, ref: ForwardRefExoticComponent<HTMLDivElement>): JSX.Element => {
  const [{ issue, selected }, setState] = useIssueHistory(({ issues, selected }) => ({
    issue: issues.get(id),
    selected: selected.has(id),
  }));
  const setShowCausedBy = useCausedByPopupState(({ setState }) => setState);

  const setDispatchIssueHistoryDrawer = useIssueHistoryDrawer(({ setState }) => setState);

  const onSelect = useCallback(
    (): void =>
      setState((current) => {
        const newSelected = new Map(current.selected);
        if (newSelected.has(id)) newSelected.delete(id);
        else newSelected.set(id, current.issues.get(id));
        return { ...current, selected: newSelected };
      }),
    [issue, id, setState]
  );
  const onEdit = (): void => {
    setDispatchIssueHistoryDrawer((current) => ({
      ...current,
      show: true,
      data: issue,
    }));
  };

  return (
    <SimpleTableRow id={id} className={getClasses(selected ? 'SELECTED' : undefined)} onDoubleClick={onEdit} ref={ref}>
      <SimpleTableCell.Select checked={selected} onChange={onSelect} />
      <SimpleTableCell name="STATION">{issue.station}</SimpleTableCell>
      <SimpleTableCell name="DATETIME">{new Datetime(issue.datetime).frontendDatetime}</SimpleTableCell>
      <SimpleTableCell name="CREATED_AT">{new Datetime().setAsUTC(issue.createdAt).toLocaleDatetime().frontendDatetime}</SimpleTableCell>
      <SimpleTableCell name="TRIP_SERVICER_IATA_AIRLINE_CODE">{issue?.tripId ? issue.tripServicerIataAirlineCode : null}</SimpleTableCell>
      <SimpleTableCell name="TRIP_FLIGHT_NAME">{issue?.tripId ? issue.tripFlightNumber : null}</SimpleTableCell>
      <SimpleTableCell name="DRIVER_NAME">{issue?.driverId ? `${issue.driverFirstName} ${issue.driverLastName}` : null}</SimpleTableCell>
      <SimpleTableCell name="CAUSED_BY">
        <div>
          {issue?.causedBy?.length
            ? (issue?.causedBy || []).map(({ causedByFirstName: firstName, causedByLastName: lastName }, idx) => {
                const name = `${firstName} ${lastName}`.trim();
                if (idx > 0) return `, ${name}`;
                return name;
              })
            : '--'}
        </div>
        {(issue?.causedBy || []).length > 1 && (
          <i
            className="fa fa-circle-info"
            onMouseOver={({ target }) => setShowCausedBy({ target: target as HTMLElement, causedBy: issue?.causedBy })}
            onMouseOut={() => setShowCausedBy(initCausedByPopupState)}
          />
        )}
      </SimpleTableCell>
      <SimpleTableCell name="ROOT_CAUSE">{issue.rootCause}</SimpleTableCell>
      <SimpleTableCell name="TAXI_USED">{issue.taxiUsed}</SimpleTableCell>
      <SimpleTableCell name="TAXI_COST">{issue?.taxiUsed ? issue.taxiCost : null}</SimpleTableCell>
      <SimpleTableCell name="RECOVERY_NEEDED">{issue.recoveryNeeded}</SimpleTableCell>
      <SimpleTableCell
        name="FLIGHT_LATE"
        style={{ color: issue.flightLate === DispatchIssueWithCacheFlightLateEnum.Yes ? 'var(--bs-danger)' : 'var(--bs-green)' }}
      >
        {issue.flightLate === DispatchIssueWithCacheFlightLateEnum.Yes ? 'Yes' : 'No'}
      </SimpleTableCell>
    </SimpleTableRow>
  );
};

export default forwardRef(IssueHistoryTableRow);
